/**
 * Вход переводчика эфира в редактор /fast ЧЕРЕЗ САЙТ (W4.4, фаза 4).
 *
 * Кнопка «Войти через сайт» уводит на torsunov.com/<lang>/editor-link?return=<этот адрес>. Сайт (где человек
 * уже вошёл своим Google) выдаёт одноразовый код и возвращает сюда с ?editor_code=…; код меняется у site_server
 * (`POST /api/auth/editor-link/redeem`) на токен редактора (`scope: stream_editor` — это НЕ вход на сайт).
 * Токен уходит заголовком `Authorization: Bearer …` во все запросы редактора эфира
 * (`https://fastapi.dev.torsunov.pro/stream_editor/v1/*`); translate-python спрашивает у site_server, кто это и
 * переводчик ли он (`/stream_editor/v1/whoami` → {email, role, features}). Новые возможности (полоса прогресса,
 * правки сами в эфир, отчёт правки) видны только при `features`. Прежний вход (логины редактора) не меняется.
 */
import { STREAM_FAST_API_URL } from "./index";

export const SITE_URL = "https://torsunov.com";
export const SITE_SERVER_AUTH_URL = "https://site-server.dev.torsunov.pro";
export const STREAM_EDITOR_PREFIX = `${STREAM_FAST_API_URL.replace(/\/$/, "")}/stream_editor/v1`;

const TOKEN_KEY = "stream_editor_token";
const EMAIL_KEY = "stream_editor_email";

export interface StreamEditorIdentity {
    email: string;
    role: "admin" | "user";
    translator: boolean;
    admin: boolean;
    features: string[];
    new_process: boolean;
}

const safeGet = (key: string): string | null => {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
};

export const getStreamEditorToken = (): string | null => safeGet(TOKEN_KEY);

export const setStreamEditorToken = (token: string, email: string): void => {
    try {
        localStorage.setItem(TOKEN_KEY, token);
        localStorage.setItem(EMAIL_KEY, email);
    } catch {
        /* storage off — the token lives only for this page */
    }
};

export const clearStreamEditorToken = (): void => {
    try {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(EMAIL_KEY);
    } catch {
        /* nothing to clear */
    }
};

/** Заголовок токена редактора — только запросам редактора эфира (translate-python `/stream_editor/v1`). */
export const authHeaderFor = (url: string): Record<string, string> => {
    const token = getStreamEditorToken();
    if (!token || !url.startsWith(STREAM_EDITOR_PREFIX)) return {};
    return { Authorization: `Bearer ${token}` };
};

let installed = false;

/**
 * Все клиенты редактора зовут `fetch(`${FAST_API_URL}/…`)` из десятков мест: заголовок ставится в одном месте —
 * обёрткой fetch, и только адресам редактора эфира. Лекции (прежний бэкенд) и чужие адреса — без изменений.
 */
export const installStreamEditorAuth = (): void => {
    if (installed || typeof window === "undefined" || typeof window.fetch !== "function") return;
    installed = true;
    const original = window.fetch.bind(window);
    window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
        const url = typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;
        const auth = authHeaderFor(url);
        if (!auth.Authorization) return original(input, init);
        const headers = new Headers(init?.headers || (typeof input !== "string" && !(input instanceof URL)
            ? input.headers : undefined));
        if (!headers.has("Authorization")) headers.set("Authorization", auth.Authorization);
        return original(input, { ...(init || {}), headers });
    };
};

/** Адрес входа через сайт: вернуться сюда же (только /fast/<медиа> — так сайт и проверяет). */
export const siteLoginUrl = (returnTo: string, lang = "ru"): string =>
    `${SITE_URL}/${lang}/editor-link?return=${encodeURIComponent(returnTo)}`;

export const startSiteLogin = (returnTo: string): void => {
    window.location.href = siteLoginUrl(returnTo);
};

/** Код из адреса (?editor_code=) и ошибка входа сайта (?editor_error=); адрес чистится от обоих. */
export const takeEditorParamsFromUrl = (): { code: string | null; error: string | null } => {
    if (typeof window === "undefined") return { code: null, error: null };
    const url = new URL(window.location.href);
    const code = url.searchParams.get("editor_code");
    const error = url.searchParams.get("editor_error");
    if (code || error) {
        url.searchParams.delete("editor_code");
        url.searchParams.delete("editor_error");
        window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
    }
    return { code, error };
};

export const redeemEditorCode = async (code: string): Promise<{ token: string; email: string }> => {
    const response = await fetch(`${SITE_SERVER_AUTH_URL}/api/auth/editor-link/redeem`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data?.site_session_token) {
        throw new Error(data?.detail || "код входа не принят — войдите через сайт ещё раз");
    }
    return { token: data.site_session_token, email: data.email || "" };
};

export class EditorAuthError extends Error {
    constructor(message: string, public status: number) {
        super(message);
    }
}

export const fetchStreamEditorWhoami = async (): Promise<StreamEditorIdentity> => {
    const response = await fetch(`${STREAM_EDITOR_PREFIX}/whoami`);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
        const detail = data?.detail;
        throw new EditorAuthError(
            (detail && (detail.message || detail)) || `вход редактора не проверен (${response.status})`,
            response.status,
        );
    }
    return data as StreamEditorIdentity;
};
